/**
 * Hub module 'ops-health' (ORGU-182): Orgus watching its own loops. Each loop reports one
 * heartbeat (when it last did its work) and a status against its threshold; a breach is a Watch
 * incident ('loop-stale', key = the loop) sent through notify.
 *
 *   GET /api/ops-health   OpsHealthState
 */
import type { Ts } from '../index.ts';

export type OpsLoop = 'watch' | 'lander' | 'follow-main' | 'main-check' | 'autopilot' | 'dead-man';

export const OPS_LOOPS: readonly OpsLoop[] = ['watch', 'lander', 'follow-main', 'main-check', 'autopilot', 'dead-man'];

export const OPS_LOOP_LABEL: Record<OpsLoop, string> = {
  watch: 'Watch',
  lander: 'Lander',
  'follow-main': 'Follow-main',
  'main-check': 'Main check',
  autopilot: 'Autopilot',
  'dead-man': 'Dead-man',
};

/** ok: within its threshold; late: past it (an incident is open); idle: nothing to do; unknown: never reported. */
export type OpsLoopStatus = 'ok' | 'late' | 'idle' | 'unknown';

export interface OpsLoopHeartbeat {
  loop: OpsLoop;
  status: OpsLoopStatus;
  /** When the loop last did its work; null when it never reported. */
  lastAt: Ts | null;
  /** How old lastAt may get before the loop is late. */
  thresholdMs: number;
  /** One short fact: "pid 4120, 2 waiting, oldest 4 min". */
  fact: string;
}

export interface OpsHealthState {
  checkedAt: Ts;
  loops: OpsLoopHeartbeat[];
}

/** A watch tick older than this is a critical incident (AC-4). */
export const OPS_WATCH_STALE_MS = 5 * 60_000;
/** A submission waiting longer than this with no landing since is a high incident (AC-1). */
export const OPS_SUBMISSION_STALE_MS = 30 * 60_000;
/** follow-main serving an older sha than main for longer than this. */
export const OPS_FOLLOW_BEHIND_MS = 30 * 60_000;
/** main-check not run on main's sha this long after main moved. */
export const OPS_MAIN_CHECK_STALE_MS = 60 * 60_000;
/** Autopilot on, no pass for this long. */
export const OPS_AUTOPILOT_STALE_MS = 15 * 60_000;
/** The dead-man notifies when the hub's health endpoint fails this long. */
export const DEAD_MAN_FAIL_MS = 2 * 60_000;
/** Where the dead-man writes its own heartbeat, under the main checkout. */
export const DEAD_MAN_FILE = '.orgus-dev/tmp/dead-man.json';

export interface DeadManFile {
  /** Last probe of the hub. */
  at: Ts;
  ok: boolean;
  /** When the current outage began, null while the hub answers. */
  downSince: Ts | null;
  /** When it last notified. */
  notifiedAt: Ts | null;
}
